import { HTMLAttributes } from 'react';
import { HeaderGroup, flexRender, Table } from '@tanstack/react-table';
import { TableHeader, TableHead, TableRow, cn } from '@sib/ui';
import { SibTableFilter } from '../../types';
import { getTableColumnPinningProperties } from '../../utils';
import { useTablePinningOptions } from './SibTableHeader.utils';

interface SibTableHeaderProps<T>
  extends HTMLAttributes<HTMLTableSectionElement> {
  headerGroups: HeaderGroup<T>[];
  table: Table<T>;
  hideRounding?: boolean;
}

export const SibTableHeader = <T,>({
  headerGroups,
  table,
  hideRounding = false,
}: SibTableHeaderProps<T>) => {
  const getTablePinningOptions = useTablePinningOptions();

  return (
    <TableHeader>
      {headerGroups.map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            const filter = header.column.columnDef.filter;
            const sorting = header.column.getIsSorted();
            const canUserSort = header.column.columnDef.sortable ?? true;
            const meta = header.column.columnDef.meta;
            const options = getTablePinningOptions<T>(header.column, table);
            const rawFilterValue = header.column.getFilterValue();
            const arrayVal = Array.isArray(rawFilterValue)
              ? rawFilterValue
              : [];
            const filterProp = filter && {
              type: filter?.type,
              value: arrayVal,
              onChange: (vals: string[]) => header.column.setFilterValue(vals),
              options: filter?.options ?? [],
              disabled: filter?.disabled,
            };
            const className = meta?.className;
            const aplicableClassName =
              typeof className === 'string'
                ? className
                : className?.(null as T);

            const { pinPosition, style } = getTableColumnPinningProperties(
              header.column,
              table
            );

            return (
              <TableHead
                key={header.id}
                canSort={canUserSort}
                style={style}
                tooltip={meta?.headerTooltip}
                onClick={() => {
                  if (!canUserSort) return;
                  if (sorting && sorting === 'desc')
                    return header.column.clearSorting();
                  if (sorting && sorting === 'asc')
                    return header.column.toggleSorting(true);
                  header.column.toggleSorting(false);
                }}
                filter={filterProp as SibTableFilter}
                clearFilter={() => header.column.setFilterValue(undefined)}
                setFilter={(vals: string[]) =>
                  header.column.setFilterValue(vals)
                }
                filterProps={filter?.FilterProps}
                options={options}
                className={cn(
                  aplicableClassName,
                  'sticky top-0 z-20 whitespace-nowrap',
                  hideRounding && 'top-10',
                  pinPosition && 'sticky z-30',
                  header.index === 0 && '!bg-primary text-white',
                  hideRounding &&
                    (header.index === 0 ||
                      header.index === headerGroup.headers.length - 1) &&
                    '!rounded-tl-none !rounded-tr-none',
                  header.index === headerGroup.headers.length - 1 &&
                    '!bg-primary text-white'
                )}
                {...(sorting && { sortDirection: sorting })}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
};
